/**
 * Which bell tab a notification belongs in: 'purchase' (Buy), 'sell' (Sell) or
 * 'bxi' (BXI).
 *
 * The tabs used to be decided purely by the notification's WORDING — a list of
 * phrases matched against the message. That worked for the legacy rows it was
 * written for, but v2 notifications are authored copy the list has never seen,
 * and the buy test is simply `/\border\b/`. So every seller-facing v2 reminder
 * — "Reminder: Voucher activation pending – Order ID - …" — was filed under Buy,
 * and the Sell tab stayed empty for notifications that had arrived. Header and
 * FullscreenNotification each kept their own copy of the phrase list, and the two
 * copies disagreed.
 *
 * A v2 row already knows its audience: the engine stamps `receiverType` when it
 * resolves the receiver. Use that first, and only fall back to wording for rows
 * that carry no audience of their own — legacy rows, including those bridged into
 * v2 as MISC.GENERIC_INFO with receiverType USER, whose tab must not move.
 */

// Listing notifications are addressed to MEMBER/BRANDWORLD, not SELLER, but they
// are about the member's own inventory — and their v2 copy ("will be delisted in
// 14 days") matches none of the legacy seller phrases either.
const LISTING_EVENT = /^(BW_LISTING|USER_LISTING)\./;

export const getNotificationMessage = (item) =>
  String(item?.message || item?.body || item?.title || '');

// The legacy wording classifier, unchanged. Kept aligned to redirect intent in
// FullscreenNotification.handleClickRedirect.
const classifyByWording = (raw) => {
  const lower = raw.toLowerCase();

  const isSell =
    lower.includes('messages') ||
    lower.includes('contacted') ||
    lower.includes('approved by our team') ||
    lower.includes('disapproved by our team') ||
    lower.includes('successfully uploaded your product') ||
    lower.includes('price and quantity') ||
    /\bpo\b/i.test(raw) ||
    lower.includes('purchase order') ||
    lower.includes('po for your product has been generated') ||
    /\bpi\b/i.test(raw) ||
    lower.includes('proforma invoice') ||
    lower.includes('has generated pi for order id') ||
    lower.includes('pi generated by you has been confirmed') ||
    lower.includes('has been accepted by the buyer and payment is done') ||
    lower.includes('your proforma invoice has been rejected') ||
    lower.includes('about to expire within 28 days') ||
    lower.includes('is about to be delisted in 14 days') ||
    lower.includes('action required! uploading of product') ||
    lower.includes('suggested some changes to your product') ||
    lower.includes('reviewed & approved by our team');
  if (isSell) return 'sell';

  const isPurchase =
    /\border\b/i.test(raw) ||
    lower.includes('has confirmed delivery') ||
    lower.includes('delivered') ||
    lower.includes('item') ||
    lower.includes('shipment') ||
    lower.includes('picked') ||
    lower.includes('awb') ||
    lower.includes('e-invoice for order') ||
    lower.includes('e-invoice of you recent order') ||
    (lower.includes('confirmed') && !lower.includes('pi generated by you'));
  if (isPurchase) return 'purchase';

  return 'bxi';
};

export const getNotificationTab = (item) => {
  const receiverType = String(item?.receiverType || '').toUpperCase();
  if (receiverType === 'SELLER') return 'sell';
  if (receiverType === 'BUYER') return 'purchase';
  if (LISTING_EVENT.test(String(item?.eventKey || ''))) return 'sell';
  return classifyByWording(getNotificationMessage(item));
};
